export interface RumErrorInput {
    customErrorType?: string;
    isPageFailure: boolean;
    context?: Record<string, unknown>;
    sanitizedMessage?: string;
}

export interface RumLike {
    addError?: (error: unknown, context?: Record<string, unknown>) => void;
    addAction?: (name: string, context?: Record<string, unknown>) => void;
    setGlobalContextProperty?: (key: string, value: string | number | null) => void;
    removeGlobalContextProperty?: (key: string) => void;
    getInternalContext?: () =>
        | {
              view?: {
                  id?: string;
              };
          }
        | undefined;
}

export const getRumErrorMessage = (error: unknown, fallbackMessage: string): string => {
    if (error instanceof Error) {
        return error.message;
    }

    return typeof error === 'string' ? error : fallbackMessage;
};

const isApolloServerResponseError = (error: unknown): boolean => {
    if (typeof error !== 'object' || error === null) return false;

    const name = 'name' in error && typeof error.name === 'string' ? error.name : '';
    const hasStatusCode = 'statusCode' in error && typeof error.statusCode === 'number';
    const hasResponseStatus =
        'response' in error &&
        typeof error.response === 'object' &&
        error.response !== null &&
        'status' in error.response &&
        typeof error.response.status === 'number';

    return (
        name === 'ServerError' || name === 'ServerParseError' || hasStatusCode || hasResponseStatus
    );
};

export const isNetworkError = (error: unknown): boolean => {
    if (typeof error === 'object' && error !== null) {
        if (
            'graphQLErrors' in error &&
            Array.isArray(error.graphQLErrors) &&
            error.graphQLErrors.length > 0
        ) {
            return false;
        }
        if ('networkError' in error && Boolean(error.networkError)) {
            return !isApolloServerResponseError(error.networkError);
        }
    }

    const message = getRumErrorMessage(error, '').toLowerCase();
    const isBrowserFetchFailure =
        error instanceof TypeError &&
        (message.includes('failed to fetch') ||
            message.includes('failed to load') ||
            message.includes('load failed'));

    return (
        isBrowserFetchFailure ||
        message.includes('network error') ||
        message.includes('networkerror')
    );
};

const sanitizeStackFrameUrl = (line: string): string =>
    line.replace(/((?:https?|file|webpack|blob):\/\/[^?#\s)]*)[?#].*?(?=:\d+:\d+\)?$)/, '$1');

const NATIVE_ERROR_NAMES = new Set([
    'Error',
    'EvalError',
    'RangeError',
    'ReferenceError',
    'SyntaxError',
    'TypeError',
    'URIError',
    'AggregateError',
]);

const getSafeErrorName = (error: Error): string => {
    const name = error.name;
    return NATIVE_ERROR_NAMES.has(name) ? name : 'Error';
};

const getSanitizedStackFrame = (line: string): string | null => {
    const location = line.match(/((?:https?|file|webpack|blob):\/\/[^\s)]+:\d+:\d+)\)?$/)?.[1];
    return location ? `    at ${sanitizeStackFrameUrl(location)}` : null;
};

/**
 * Reports a feature error and lets the host aggregate page failures.
 * Use sanitizedMessage when the source error may contain user or server data.
 */
export const logRumError = (
    rum: RumLike | undefined,
    error: unknown,
    { customErrorType, isPageFailure, context, sanitizedMessage }: RumErrorInput,
): void => {
    let reportedError: Error;
    const fallbackMessage =
        sanitizedMessage !== undefined && sanitizedMessage.trim().length > 0
            ? sanitizedMessage
            : (customErrorType ?? 'Error');
    try {
        const message = getRumErrorMessage(error, customErrorType ?? 'Error');
        if (sanitizedMessage !== undefined) {
            reportedError = new Error(fallbackMessage);
            if (error instanceof Error) {
                reportedError.name = getSafeErrorName(error);
                const stackFrames = error.stack
                    ?.split('\n')
                    .map(getSanitizedStackFrame)
                    .filter((line): line is string => line !== null);
                if (stackFrames && stackFrames.length > 0) {
                    reportedError.stack = `${reportedError.name}: ${fallbackMessage}\n${stackFrames.join('\n')}`;
                }
            }
        } else if (error instanceof Error) {
            reportedError = error;
        } else {
            reportedError = new Error(message);
        }
    } catch {
        console.error('[DatadogRUM] Failed to inspect feature error');
        reportedError = new Error(fallbackMessage);
    }
    try {
        rum?.addError?.(reportedError, {
            ...context,
            ...(customErrorType ? { customErrorType } : {}),
            pageFailure: isPageFailure,
        });
    } catch (telemetryError) {
        console.error('[DatadogRUM] Failed to report feature error', telemetryError);
    }
};
